import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import {
  VirtualizedFormSelect,
  Checkbox,
  CollapsibleSection,
} from '@roger/ui-components';
import type { KinnserClinicDataResponse } from '../../services/api/types.gen';
import { Field, DateField } from '../../components/FormFields';
import { AiExtractedInsuranceSuggestion } from '../../components/intake/AiExtractedInsuranceSuggestion';
import { KinnserAdmissionSourceField } from '../../components/intake/KinnserAdmissionSourceField';
import { KinnserReferralSourceFields } from '../../components/intake/KinnserReferralSourceFields';
import {
  PatientDetailsSection,
  EmergencyContactSection,
} from '../../components/intake/shared';
import {
  ADMISSION_TYPE_OPTIONS,
  HCPCS_CODE_OPTIONS,
} from '../../hooks/useCreatePatientForm';
import type { AddressValidationResult } from '../../hooks/useCreatePatientForm/types';
import type { CreateEpisodeStepFormData } from '../../schemas/createEpisodeStepFormSchema';
import {
  ClinicSelect,
  type ClinicSelectClinic,
} from '../../components/clinic/ClinicSelect';

interface KinnserCreateEpisodeStepFormContentProps {
  // Form controls
  control: Control<CreateEpisodeStepFormData>;
  register: UseFormRegister<CreateEpisodeStepFormData>;
  errors: FieldErrors<CreateEpisodeStepFormData>;
  setValue: UseFormSetValue<CreateEpisodeStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;

  // Display values
  displayValues: {
    hhCahpsOptOut?: boolean;
    kinnserLanguageKey?: string;
  };

  // Save status
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  saveToServer: (data: Partial<CreateEpisodeStepFormData>) => void;

  // Clinics
  clinics: ClinicSelectClinic[];
  clinicData: KinnserClinicDataResponse | null;
  loadingClinics: boolean;
  loadingClinicData: boolean;
  insuranceOptions: { name: string; id: string }[];

  // Intake status
  selectedIntake: {
    status: string;
    primary_insurance?: string | null;
    fields_explanations?: Record<string, unknown> | null;
    is_new_patient?: boolean | null;
    // Read for the Admission Source notice: the UB-04 code intake captured,
    // which lives in the JSONB blob with a top-level mirror.
    discharge_facility_type?: string | null;
    // The saved override. Read alongside the captured value because it is what
    // the push actually resolves to when set, so it is the value whose
    // sendability the notice has to judge.
    kinnser_admission_source?: string | null;
    fields?: Record<string, unknown> | null;
  } | null;
  showAIExplanations?: boolean;
  documentNameDict?: Record<string, string>;

  // Address validation
  addressSuggestion: AddressValidationResult | null;
  addressModalOpen: boolean;
  setAddressModalOpen: (open: boolean) => void;
  isValidatingAddress: boolean;
  handleAddressFieldBlur: (
    fieldNameOrEvent: React.FocusEvent<HTMLElement>
  ) => void;
  handleAcceptAddressSuggestion: () => void;

  // Clinic field handler
  handleClinicFieldBlur: () => void;

  // Language key for conditional rendering
  kinnserLanguageKey: string | null | undefined;

  // Form validation state
  isFormValid: boolean;
}

export const KinnserCreateEpisodeStepFormContent: React.FC<
  KinnserCreateEpisodeStepFormContentProps
> = ({
  control,
  register,
  errors,
  setValue,
  handleFieldBlur,
  saveToServer,
  clinics,
  clinicData,
  loadingClinics,
  loadingClinicData,
  insuranceOptions,
  selectedIntake,
  showAIExplanations = false,
  documentNameDict,
  isValidatingAddress,
  handleAddressFieldBlur,
  kinnserLanguageKey,
}) => {
  const hasTransferredFromAnotherHha = useWatch({
    control,
    name: 'has_transferred_from_another_hha',
    defaultValue: false,
  });

  // Branch renders `display: none` for a clinic with one branch, so it takes
  // no grid track. Its row must then divide by three, not four.
  const branchHidden = Boolean((clinicData?.branches?.length ?? 0) <= 1);

  return (
    <form className='space-y-5' onSubmit={e => e.preventDefault()}>
      {/* Kinnser Assignment */}
      <CollapsibleSection title='Kinnser Assignment'>
        <div className='space-y-3'>
          {/* Each row is its own grid, so it divides its own width by the
              number of fields it actually holds. One shared `grid-cols-4`
              auto-flow grid left a dead trailing column on every row holding
              fewer than four items, and this row normally holds three: Branch
              renders `display: none` for a single-branch clinic and so takes
              no grid track. That left Insurance — and MRN on the row below —
              ending 98px short of the card's right edge. */}
          <div
            className={`grid gap-3 ${branchHidden ? 'grid-cols-3' : 'grid-cols-4'}`}
          >
            {/* Row 1: Clinic & Branch */}
            <ClinicSelect
              label='Clinic'
              placeholder={
                loadingClinics ? 'Loading clinics...' : 'Select clinic...'
              }
              clinics={clinics}
              required
              disabled
              error={errors.agency_clinic_id?.message}
              {...register('agency_clinic_id')}
            />

            <VirtualizedFormSelect
              label='Branch'
              placeholder={
                loadingClinicData ? 'Loading branches...' : 'Select branch...'
              }
              options={clinicData?.branches || []}
              required
              disabled={loadingClinicData}
              error={errors.kinnser_branch_id?.message}
              {...register('kinnser_branch_id', {
                onBlur: handleFieldBlur,
              })}
              hidden={branchHidden}
            />

            <VirtualizedFormSelect
              label='Physician'
              placeholder={
                loadingClinicData
                  ? 'Loading physicians...'
                  : 'Select physician...'
              }
              options={clinicData?.physicians || []}
              sortOptions
              required
              disabled={loadingClinicData}
              error={errors.kinnser_physician_id?.message}
              {...register('kinnser_physician_id', {
                onBlur: handleFieldBlur,
              })}
            />

            <div>
              <VirtualizedFormSelect
                label='Insurance Provider'
                placeholder={
                  loadingClinicData
                    ? 'Loading insurance providers...'
                    : 'Select insurance...'
                }
                options={insuranceOptions}
                required
                disabled
                error={errors.kinnser_insurance_provider_id?.message}
                {...register('kinnser_insurance_provider_id')}
              />
              <AiExtractedInsuranceSuggestion
                value={selectedIntake?.primary_insurance}
                showAIExplanations={showAIExplanations}
                fieldExplanation={
                  selectedIntake?.fields_explanations?.primary_insurance
                }
                fieldsExplanations={selectedIntake?.fields_explanations}
                documentNameDict={documentNameDict}
              />
            </div>
          </div>

          <div className='grid grid-cols-3 gap-3'>
            {/* Row 3: Admission details */}
            <VirtualizedFormSelect
              label='Admission Type'
              placeholder='Select type...'
              options={ADMISSION_TYPE_OPTIONS}
              required
              disabled={loadingClinics || loadingClinicData}
              error={errors.kinnser_admission_type?.message}
              {...register('kinnser_admission_type', {
                onBlur: handleFieldBlur,
              })}
            />

            <VirtualizedFormSelect
              label='HCPCS Code'
              placeholder='Select code...'
              options={HCPCS_CODE_OPTIONS}
              disabled={loadingClinics || loadingClinicData || false}
              required
              error={errors.kinnser_hcpcs_code_key?.message}
              {...register('kinnser_hcpcs_code_key', {
                onBlur: handleFieldBlur,
              })}
            />

            <Field
              label='MRN'
              htmlFor='kinnserMrn'
              disabled={
                clinicData?.auto_mrn ||
                loadingClinics ||
                loadingClinicData ||
                false
              }
              required={!clinicData?.auto_mrn}
              error={
                !clinicData?.auto_mrn ? errors.kinnser_mrn?.message : undefined
              }
              inputProps={{
                ...register('kinnser_mrn', {
                  onBlur: handleFieldBlur,
                }),
                ...(clinicData?.auto_mrn
                  ? { placeholder: 'Auto-assigned' }
                  : {}),
              }}
            />

            {/* Medicaid field hidden for now */}
            {/* <Field
                label='Medicaid Number'
                htmlFor='medicaidNumber'
                disabled={loadingClinics || loadingClinicData}
                inputProps={register('medicaid_id', {
                  onBlur: handleFieldBlur,
                })}
              /> */}
          </div>

          {/* The referral pair and its behavior are shared with the scheduling
              tab's existing-patient new-admission surface (EMR-1534); the
              layout note about the HH CAHPS checkbox below it lives with the
              component.

              Admission Source rides in as the row's leading field, so the three
              sit on one line in the order product asked for. The row's own
              "left unset" line stays off here: allowClear is on for this
              surface, where an empty select really does mean no referral. */}
          <KinnserReferralSourceFields<CreateEpisodeStepFormData>
            control={control}
            register={register}
            setValue={setValue}
            errors={errors}
            handleFieldBlur={handleFieldBlur}
            saveToServer={saveToServer}
            clinicData={clinicData}
            loadingClinicData={loadingClinicData}
            leadingField={
              <KinnserAdmissionSourceField<CreateEpisodeStepFormData>
                control={control}
                register={register}
                errors={errors}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                disabled={loadingClinics || loadingClinicData}
                selectedIntake={selectedIntake}
                // New patient: nothing is sent when this is left unset, so
                // WellSky applies its own default. The existing-patient
                // scheduling surface says the opposite -- see the prop.
                unsetOutcome='wellsky_default'
              />
            }
          />

          <div className='flex items-center gap-2 pt-1'>
            <Controller
              name='kinnser_hh_cahps_opt_out'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  id='hhCahpsOptOut'
                  checked={value || false}
                  disabled={loadingClinics || loadingClinicData}
                  onCheckedChange={checked => {
                    onChange(checked);
                    if (checked) {
                      saveToServer({
                        kinnser_hh_cahps_opt_out: true,
                      });
                    } else {
                      saveToServer({
                        kinnser_hh_cahps_opt_out: false,
                      });
                    }
                  }}
                />
              )}
            />
            <label
              htmlFor='hhCahpsOptOut'
              className='text-sm font-medium cursor-pointer'
            >
              HH CAHPS Opt Out
            </label>
          </div>
        </div>
      </CollapsibleSection>

      {/* Transfer & Face-to-Face */}
      <CollapsibleSection title='Transfer & Face-to-Face'>
        <div className='grid grid-cols-4 gap-3'>
          <div className='col-span-4 flex items-center gap-2 pt-1'>
            <Controller
              name='has_transferred_from_another_hha'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  id='hasTransferredFromAnotherHhaKinnser'
                  checked={value ?? false}
                  onCheckedChange={checked => {
                    const newVal = checked === true;
                    onChange(newVal);
                    if (!newVal) {
                      setValue('prior_hha', null);
                      saveToServer({
                        has_transferred_from_another_hha: false,
                        prior_hha: null,
                      });
                    } else {
                      saveToServer({
                        has_transferred_from_another_hha: true,
                      });
                    }
                  }}
                />
              )}
            />
            <label
              htmlFor='hasTransferredFromAnotherHhaKinnser'
              className='text-sm font-medium cursor-pointer'
            >
              Transferred from another HHA
            </label>
          </div>
          {hasTransferredFromAnotherHha === true && (
            <Controller
              name='prior_hha'
              control={control}
              render={({ field: { value, onChange } }) => (
                <Field
                  label='Prior HHA'
                  htmlFor='prior_hha_kinnser'
                  inputProps={{
                    value: value ?? '',
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(e.target.value || null);
                      saveToServer({
                        prior_hha: e.target.value || null,
                      });
                    },
                    onBlur: handleFieldBlur,
                  }}
                />
              )}
            />
          )}
          <Controller
            name='f2f_encounter_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                fieldAnchor='f2f_encounter_date'
                label='Face-to-Face Encounter Date'
                value={value}
                htmlFor='f2f_encounter_date_kinnser'
                onChange={val => {
                  onChange(val);
                  saveToServer({ f2f_encounter_date: val });
                }}
                onBlur={onBlur}
                yearRange={{
                  fromYear: new Date().getFullYear() - 10,
                  toYear: new Date().getFullYear() + 1,
                }}
              />
            )}
          />
        </div>
      </CollapsibleSection>

      {/* Patient Details - Combined patient info and contact */}
      <PatientDetailsSection
        control={control}
        register={register}
        errors={errors}
        setValue={setValue}
        handleFieldBlur={handleFieldBlur}
        saveToServer={saveToServer}
        isValidatingAddress={isValidatingAddress}
        handleAddressFieldBlur={handleAddressFieldBlur}
        kinnserLanguageKey={kinnserLanguageKey}
      />

      {/* Emergency Contact - optional; pushed to WellSky when create/onboard */}
      <EmergencyContactSection
        control={control}
        register={register}
        errors={errors}
        handleFieldBlur={handleFieldBlur}
      />
    </form>
  );
};
